import {
  ApiOrg,
  ApiOrgContractor,
  ApiOrgContractorsMap,
  ApiOrgProperty,
  ApiPropertyProject,
  ApiScan,
  ApiScanNote,
  ScopeReportLineItem,
  ScopeReportOverview,
} from '@kaiizen/kai-pro-api';
import { sortLineItemsByMultipleFields } from '@kaiizen/kai-reports';
import {
  getAllScopedLineItems,
  getGroupRoomAreaForReport,
  getGroupsInStandardGroupOrder,
  getLineItemsInStandardSortOrder,
  getOptionsInStandardSortOrder,
  getTasksInStandardSortOrder,
  isGroupScoped,
  isGroupShownOnScopeReport,
  isLineItemScoped,
  isOptionScoped,
  isOtherTask,
  isScanPartWorkNeedsRework,
  isTaskScoped,
  isTaskShownOnScopeReport,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import {
  categorizeLineItem,
  extractLineItemCosts,
  findLineItemKey,
  formatCalendarDate,
  formatDate,
  getContractorIdForTask,
  getFirstAssignedGeneralContractor,
  getGlCode,
  getGroupName,
  getLineItemName,
  getLineItemQuantity,
  getNumericValue,
  getOptionName,
  getScopePDFTaskDescName,
  getStatusTaskLabel,
  getStatusTaskValue,
  getTaskDescription,
  getTaskName,
  getTaskWorkStatus,
  getTradeForTask,
  isStatusTask,
  parseMetadataNumber,
  taskHasOptionsWithLineItems,
  truncateText,
} from './ScopeReportLib';
import {
  AssignedContractorsMap,
  BillingExcelLineItem,
  GroupedReportLineItems,
  OrgProductExistsLookup,
  ScanPartNotesLookup,
  ScopeReportLineItemWithChangeData,
  TaskPhotoLookup,
  TaskPhotoWithName,
} from './ScopeReportTypes';

/**
 * Builds and diffs scope report line items from scan data.
 *
 * Scan tag keys (`deferred`, `tenantResponsibility`, org-specific modifier tags) and report fields
 * (`isDeferredLineItem`, `modifierTags`, `changedColumns.deferred`) are internal identifiers —
 * not user-visible copy. Display names for modifiers are resolved later via org settings where
 * artifacts are rendered (HTML, Excel, React).
 */

// ***********************************************************************************
// Helper Functions for Data Transformation
// ***********************************************************************************

const calculateBathCount = (customerMetadata?: { fullBathCount?: number | null; halfBathCount?: number | null }): number | undefined => {
  const fullBaths = customerMetadata?.fullBathCount;
  const halfBaths = customerMetadata?.halfBathCount;
  if (fullBaths == null && halfBaths == null) return undefined;
  return (fullBaths ?? 0) + (halfBaths ?? 0) * 0.5;
};

/**
 * Type for additional filters that can be passed to the function.
 * These filters return true to EXCLUDE items.
 */
export type ScopeReportFilters = {
  groupFilters?: ((group: ScanGroupOptimized) => boolean)[];
  taskFilters?: ((task: ScanTaskOptimized) => boolean)[];
  optionFilters?: ((option: ScanOptionOptimized) => boolean)[];
  lineItemFilters?: ((lineItem: ScanLineItemOptimized) => boolean)[];
};

/**
 * The text an artifact prints for a part's notes. Status notes often have no text: they still set
 * the edit-request flags, but print nothing.
 */
const getPrintedNoteBodies = (notes: ApiScanNote[]): string[] => notes.map(note => note.body).filter(body => body.trim() !== '');

/**
 * The tags the line item actually carries, as a list of **lineItemTag keys** (e.g. `deferred`).
 *
 * These strings are not shown to users; UIs map them to org `displayName` values. Carried on the
 * report because an org's own modifiers have no fixed field to live in. Not a historical snapshot:
 * a `ScopeReportLineItem` is built on demand from the live scan and never stored, so this always
 * reflects the scan as it is now, and renaming or retiring a modifier changes what a regenerated
 * report shows (KAIDEV-4206, review round 1).
 */
const getSetTags = (tags: Record<string, unknown> | undefined): string[] =>
  Object.entries(tags ?? {})
    .filter(([, isSet]) => isSet === true)
    .map(([tag]) => tag);

/**
 * Transform scan data into ScopeReportLineItem[]
 *
 * - Uses isGroupScoped, isTaskScoped, isOptionScoped, isLineItemScoped from StandardScanGetters
 * - Filters out zero-quantity line items
 * - Supports additional custom filters via the filters parameter
 *
 * @param optimizedScan - The optimized scan data to transform
 * @param contractorsMap - The contractors map to use to resolve contractor IDs to names
 * @param getTaskPhotos - Function to resolve task photos (returns array)
 * @param getScanNotesByScanPartId - Function to lookup notes by scanPartId (group.id, task.id, option.id)
 * @param filters - Optional additional filter functions to exclude items (returning true excludes)
 * @param getOrgProductExists - Optional lookup: returns true if the org product still exists in the database
 * @returns ScopeReportLineItem[] (sorted with sortLineItemsByMultipleFields)
 *
 * Populates `isDeferredLineItem` / `isTenantRespLineItem` from scan tag booleans and `modifierTags`
 * from {@link getSetTags}; those names are data flags and tag keys, not printed labels.
 */
export const buildScopeReportLineItems = (
  optimizedScan: ScanRootOptimized,
  contractorsMap: ApiOrgContractorsMap,
  getTaskPhotos: TaskPhotoLookup,
  getScanNotesByScanPartId: ScanPartNotesLookup,
  getOrgProductExists?: OrgProductExistsLookup,
  filters: ScopeReportFilters = {},
): ScopeReportLineItem[] => {
  const allLineItems: ScopeReportLineItem[] = [];
  if (!optimizedScan.groups) return allLineItems;

  const { clusterOrder, groupClusters } = getGroupsInStandardGroupOrder(optimizedScan.template, optimizedScan.groups, group =>
    isGroupShownOnScopeReport(group)
    && !filters.groupFilters?.some(f => f(group)));

  for (const cluster of clusterOrder) {
    for (const group of groupClusters[cluster]) {
      if (!isGroupShownOnScopeReport(group) || filters.groupFilters?.some(f => f(group))) continue;
      const lineItemGroupPartial = {
        groupId: group.id,
        groupTemplateId: group.templateId,
        groupName: getGroupName(group),
        groupRoomArea: getGroupRoomAreaForReport(group),
        groupNotes: getPrintedNoteBodies(getScanNotesByScanPartId(group.id)),
      };

      const { clusterOrder, taskClusters } = getTasksInStandardSortOrder(group.template, group.tasks, task =>
        isTaskShownOnScopeReport(task)
        && !filters.taskFilters?.some(f => f(task)));

      for (const cluster of clusterOrder) {
        for (const task of taskClusters[cluster]) {
          const contractorName = getContractorName(contractorsMap, getContractorIdForTask(task, optimizedScan));
          const taskApiNotes = getScanNotesByScanPartId(task.id);
          const isEditRequest = taskApiNotes.some(note => note.scanNoteType === 'editRequest');
          const isEditResolution = taskApiNotes.some(note => note.scanNoteType === 'editResolution');
          const isAdminEditResolution = taskApiNotes.some(note => note.scanNoteType === 'approval');
          const lineItemTaskPartial = {
            ...lineItemGroupPartial,
            taskId: task.id,
            taskTemplateId: task.templateId,
            contractorName,
            taskNotes: getPrintedNoteBodies(
              isEditRequest && isEditResolution && isAdminEditResolution
                ? taskApiNotes.filter(note =>
                  note.scanNoteType !== 'editRequest'
                  && note.scanNoteType !== 'editResolution'
                  && note.scanNoteType !== 'approval'
                )
                : taskApiNotes,
            ),
            isEditRequest,
            isEditResolution,
            isAdminEditResolution: isEditRequest && isEditResolution && isAdminEditResolution,
            taskName: getTaskName(task),
            taskDescription: getTaskDescription(task),
            taskWorkStatus: getTaskWorkStatus(task),
            trade: getTradeForTask(task),
            taskPhotos: getTaskPhotos(task) ?? [],
            isPunchlistTask: isScanPartWorkNeedsRework(task),
            isStatusTask: isStatusTask(task),
            isOtherTask: isOtherTask(task),
            statusValue: getStatusTaskValue(task),
            statusLabel: getStatusTaskLabel(task),
            glCode: getGlCode(task),
          };

          // the definition of status tasks changed on 02/01/2026 to where they could
          // have options with line items so we keep this function around to handle
          // templates that were created before the change and validation
          if (lineItemTaskPartial.isStatusTask && !taskHasOptionsWithLineItems(task)) {
            const optionName = lineItemTaskPartial.taskName; // no option for status task default to task name
            const lineItemName = lineItemTaskPartial.taskName; // no line item for status task default to task name
            const scopePDFTaskDescName = lineItemTaskPartial.taskName; // no scope PDF task description for status task default to task name

            allLineItems.push({
              ...lineItemTaskPartial,

              optionId: '', // no option for status tasks
              optionName,
              optionTemplateId: '', // no option for status tasks

              lineItemId: '',
              lineItemName,
              lineItemTemplateId: '', // no line item for status tasks
              lineItemNotes: [],

              unitPrice: 0,
              totalLineItemCost: 0,
              scopePDFTaskDescName,

              productQuantity: 0,
              productQuantityValueType: 'each',
            });
            continue;
          }

          const options = getOptionsInStandardSortOrder(task.template, task.options, option =>
            isOptionScoped(option)
            && !filters.optionFilters?.some(f => f(option)));

          for (const option of options) {
            const lineItems = getLineItemsInStandardSortOrder(option.template, option.lineItems, lineItem =>
              isLineItemScoped(lineItem)
              && !filters.lineItemFilters?.some(f => f(lineItem)));

            for (const lineItem of lineItems) {
              allLineItems.push({
                ...lineItemTaskPartial,

                // option fields
                optionId: option.id,
                optionTemplateId: option.templateId,
                optionName: getOptionName(option),

                // line item fields
                lineItemId: lineItem.id,
                lineItemTemplateId: lineItem.templateId,
                lineItemName: getLineItemName(lineItem),
                lineItemNotes: getPrintedNoteBodies(getScanNotesByScanPartId(option.id)),

                isDeferredLineItem: lineItem.tags?.deferred === true,
                isTenantRespLineItem: lineItem.tags?.tenantResponsibility === true,
                modifierTags: getSetTags(lineItem.tags),

                ...extractLineItemCosts(lineItem, categorizeLineItem(lineItem)),
                scopePDFTaskDescName: getScopePDFTaskDescName(task, option, lineItem),
                yardiDescription: truncateText(`${getGroupName(group)} - ${getScopePDFTaskDescName(task, option, lineItem)}`, 79),

                // product fields
                productName: lineItem.skuDetails?.title,
                productSku: lineItem.skuDetails?.skus?.[0],
                productBrand: lineItem.skuDetails?.brand,
                productExists: lineItem.skuDetails?.id != null ? getOrgProductExists?.(lineItem.skuDetails.id) : undefined,
                productQuantity: getLineItemQuantity(lineItem),
                productQuantityValueType: lineItem.template?.values?.quantity?.type || '',
              });
            }
          }
        }
      }
    }
  }
  return sortLineItemsByMultipleFields(allLineItems);
};

export const getContractorName = (contractorsMap: Record<string, ApiOrgContractor>, contractorId: string): string => {
  if (contractorId === 'unassigned') return 'Unassigned';
  return contractorsMap[contractorId]?.name || `Unknown Contractor (${contractorId})`;
};

/**
 * Build a set of assigned contractors from actual task assignments
 *
 * This function scans through all tasks in the optimized scan and collects
 * the unique contractors that are actually assigned to tasks (via task.assignedContractorId).
 * Unlike buildDefaultContractorsByTrade which uses the scan's default contractors,
 * this returns the contractors that are actively assigned to work.
 *
 * @param optimizedScan - The optimized scan data containing groups and tasks
 * @param getContractorName - Function to resolve contractor IDs to names
 * @returns Record mapping contractor IDs to contractor info with trade and name
 */
export const buildAssignedContractorsWithTrades = (
  optimizedScan: ScanRootOptimized,
  contractorsMap: ApiOrgContractorsMap,
): AssignedContractorsMap => {
  const result: AssignedContractorsMap = {};

  (optimizedScan.groups ?? []).forEach(group => {
    if (!isGroupScoped(group)) return;
    (group.tasks ?? []).forEach(task => {
      if (!isTaskScoped(task)) return;
      const contractorId = getContractorIdForTask(task, optimizedScan);
      if (contractorId && contractorId !== 'unassigned') {
        const name = getContractorName(contractorsMap, contractorId);
        const assignedTaskTrade = getTradeForTask(task);
        const existingTrades = result[contractorId]?.trades || [];
        result[contractorId] = {
          trades: [...new Set([...existingTrades, assignedTaskTrade])],
          name,
        };
      }
    });
  });
  return result;
};

/**
 * Build a set of assigned contractors from actual task assignments
 *
 * @deprecated Use `buildAssignedContractorsWithTrades` instead. This function returns a trade-based mapping
 * but with empty trade strings. The new function returns `AssignedContractorsMap` with proper trade arrays.
 *
 * @param optimizedScan - The optimized scan data containing groups and tasks
 * @param contractorsMap - Map of contractor IDs to contractor info
 * @returns Record mapping contractor IDs to contractor info with trade and name (trade will be empty string)
 */
export const buildAssignedContractors = (
  optimizedScan: ScanRootOptimized,
  contractorsMap: ApiOrgContractorsMap,
): Record<string, { trade: string; name: string }> => {
  const result: Record<string, { trade: string; name: string }> = {};

  (optimizedScan.groups ?? []).forEach(group => {
    (group.tasks ?? []).forEach(task => {
      const contractorId = getContractorIdForTask(task, optimizedScan);
      if (contractorId && contractorId !== 'unassigned') {
        const name = getContractorName(contractorsMap, contractorId);
        result[contractorId] = {
          trade: '',
          name,
        };
      }
    });
  });
  return result;
};

/**
 * Compute change order diff between current and baseline SOW data
 *
 * This function compares two flat arrays of line items and identifies:
 * - Line items that have changed (contractor, quantity, rate, amount, or product details)
 * - New line items that don't exist in baseline
 * - Removed line items that exist in baseline but not in current
 *
 * Returns all line items from current data with changeOrderData attached for items that changed,
 * plus deleted items from baseline that no longer exist in current.
 *
 * @param currentSowData - Current change request SOW data (flat array)
 * @param baselineSowData - Baseline (parent scan) SOW data (flat array)
 * @returns Array of line items with change data attached where applicable
 *
 * `changeOrderData.changedColumns.deferred` / `tenantResponsibility` are column ids that select
 * highlighted cells and the modifier names listed in the change-type label (`getChangeTypeLabel`);
 * the ids themselves are never printed.
 */
export const computeDiff = (
  currentSowData: ScopeReportLineItem[],
  baselineSowData: ScopeReportLineItem[],
): ScopeReportLineItemWithChangeData[] => {
  const baselineItemsByKey = buildLineItemKeyMap(baselineSowData);
  const currentItemsByKey = buildLineItemKeyMap(currentSowData);
  const resultLineItems: ScopeReportLineItemWithChangeData[] = [];

  currentSowData.forEach(currentItem => {
    const key = findLineItemKey(currentItem);
    const baselineItem = baselineItemsByKey.get(key);

    if (baselineItem) {
      const changeData = computeLineItemChangeData(currentItem, baselineItem);
      if (changeData) {
        resultLineItems.push({ ...currentItem, changeOrderData: changeData });
      } else {
        resultLineItems.push(currentItem);
      }
    } else {
      resultLineItems.push({
        ...currentItem,
        changeOrderData: {
          changeType: 'new',
          changedColumns: {
            contractor: false,
            quantity: false,
            rate: false,
            amount: true,
            deferred: false,
            tenantResponsibility: false,
          },
        },
      });
    }
  });

  baselineSowData.forEach(baselineItem => {
    const key = findLineItemKey(baselineItem);
    if (!currentItemsByKey.has(key)) {
      resultLineItems.push({
        ...baselineItem,
        productQuantity: 0,
        totalLineItemCost: 0,
        changeOrderData: {
          baselineTotalPrice: baselineItem.totalLineItemCost || 0,
          baselineContractorName: baselineItem.contractorName || '',
          baselineQuantity: baselineItem.productQuantity || 0,
          baselineUnitPrice: baselineItem.unitPrice || 0,
          changeType: 'deleted',
          changedColumns: {
            contractor: false,
            quantity: true,
            rate: false,
            amount: true,
            deferred: false,
            tenantResponsibility: false,
          },
        },
      });
    }
  });

  return resultLineItems;
};

const buildLineItemKeyMap = (items: ScopeReportLineItem[]): Map<string, ScopeReportLineItem> => {
  const map = new Map<string, ScopeReportLineItem>();
  items.forEach(item => map.set(findLineItemKey(item), item));
  return map;
};

/** Builds change-order diff metadata; `changedColumns` keys are internal column ids, not UI copy. */
const computeLineItemChangeData = (
  currentItem: ScopeReportLineItem,
  baselineItem: ScopeReportLineItem,
): ScopeReportLineItemWithChangeData['changeOrderData'] | null => {
  const baselineTotal = baselineItem.totalLineItemCost || 0;
  const currentTotal = currentItem.totalLineItemCost || 0;
  const baselineContractor = baselineItem.contractorName || '';
  const currentContractor = currentItem.contractorName || '';
  const baselineQuantity = baselineItem.productQuantity || 0;
  const currentQuantity = currentItem.productQuantity || 0;
  const baselineRate = baselineItem.unitPrice || 0;
  const currentRate = currentItem.unitPrice || 0;

  const contractorChanged = baselineContractor !== currentContractor;
  const quantityChanged = Math.abs(baselineQuantity - currentQuantity) > 0.01;
  const rateChanged = Math.abs(baselineRate - currentRate) > 0.01;
  const amountChanged = Math.abs(baselineTotal - currentTotal) > 0.01;
  const baselineDeferred = baselineItem.isDeferredLineItem === true;
  const currentDeferred = currentItem.isDeferredLineItem === true;
  const deferredChanged = baselineDeferred !== currentDeferred;
  // KAIDEV-4181: tenantResponsibility is an informational tag and is not a
  // change-order trigger, so a change to it is never counted as a change here.

  const hasProductChanges = baselineItem.productName !== currentItem.productName
    || baselineItem.productSku !== currentItem.productSku
    || baselineItem.productBrand !== currentItem.productBrand;

  const hasAnyChange = contractorChanged || quantityChanged || rateChanged || amountChanged
    || deferredChanged || hasProductChanges;

  if (!hasAnyChange) return null;

  return {
    baselineTotalPrice: baselineTotal,
    baselineContractorName: baselineContractor,
    baselineQuantity: baselineQuantity,
    baselineUnitPrice: baselineRate,
    changeType: 'existing',
    changedColumns: {
      contractor: contractorChanged,
      quantity: quantityChanged,
      rate: rateChanged,
      amount: amountChanged,
      deferred: deferredChanged,
      tenantResponsibility: false, // KAIDEV-4181: never highlighted as a change
    },
  };
};

/**
 * Compute an accounting ledger-style diff between current and baseline SOW data.
 *
 * Unlike `computeDiff` which shows the current state with change metadata attached,
 * this function produces journal-entry style output where:
 *
 * - **Unchanged items**: Included without changeOrderData (same as computeDiff)
 * - **New items**: Single positive entry with current values
 * - **Deleted items**: Single negative entry (negative quantity and totalLineItemCost)
 * - **Modified items**: Two entries (reversal + restatement pattern)
 *   - Reversal: Negative entry with baseline values negated
 *   - Restatement: Positive entry with current values
 *
 * This approach follows standard accounting practices where modifications are recorded
 * as a reversal of the original entry followed by a new entry at the updated values.
 * The sum of all ledger entries (those with changeOrderData) equals the net change in scope value.
 *
 * @param currentSowData - Current change request SOW data (flat array)
 * @param baselineSowData - Baseline (parent scan) SOW data (flat array)
 * @returns Array of line items with changeOrderData using 'new', 'deleted', 'reversal', or 'restatement' changeTypes
 */
export const computeDiffLedgerReversalAndRestatement = (
  currentSowData: ScopeReportLineItem[],
  baselineSowData: ScopeReportLineItem[],
): ScopeReportLineItemWithChangeData[] => {
  const baselineItemsByKey = buildLineItemKeyMap(baselineSowData);
  const currentItemsByKey = buildLineItemKeyMap(currentSowData);
  const resultItems: ScopeReportLineItemWithChangeData[] = [];

  currentSowData.forEach(currentItem => {
    const key = findLineItemKey(currentItem);
    const baselineItem = baselineItemsByKey.get(key);

    if (baselineItem) {
      const modificationEntries = computeLedgerModificationEntries(currentItem, baselineItem);
      if (modificationEntries.length > 0) {
        resultItems.push(...modificationEntries);
      } else {
        resultItems.push(currentItem);
      }
    } else {
      resultItems.push({
        ...currentItem,
        changeOrderData: {
          changeType: 'new',
          changedColumns: { contractor: false, quantity: false, rate: false, amount: true, deferred: false, tenantResponsibility: false },
        },
      });
    }
  });

  baselineSowData.forEach(baselineItem => {
    const key = findLineItemKey(baselineItem);
    if (!currentItemsByKey.has(key)) {
      resultItems.push({
        ...baselineItem,
        productQuantity: -(baselineItem.productQuantity || 0),
        totalLineItemCost: -(baselineItem.totalLineItemCost || 0),
        changeOrderData: {
          changeType: 'deleted',
          changedColumns: { contractor: false, quantity: true, rate: false, amount: true, deferred: false, tenantResponsibility: false },
        },
      });
    }
  });

  return resultItems;
};

/**
 * Compute ledger-style diff using blended rate for a single-line net change view.
 *
 * For modifications, outputs a single line item showing the net change:
 * - productQuantity: delta (current - baseline)
 * - unitPrice: blended rate that makes the math work (deltaAmount / deltaQty)
 * - totalLineItemCost: delta (current - baseline)
 *
 * When only quantity changes, blended rate equals the actual unit price.
 * When only rate changes (qty delta = 0), shows the delta rate with qty=currentQuantity.
 * When both change, shows a computed blended rate.
 *
 * Baseline deferred lines that are no longer deferred are treated as never having been fully
 * reflected in Yardi: the output row keeps the **current** quantity, unit price, and total
 * (no delta / blended-net math).
 *
 * Lines whose contractor changed also keep the **current** quantity, unit price, and total: the
 * new contractor's PO carries the full line and processContractorOnlyChanges adds the baseline
 * reversal for the old contractor.
 *
 * @param currentSowData - Current change request SOW data (flat array)
 * @param baselineSowData - Baseline (parent scan) SOW data (flat array)
 * @returns Array of line items with changeOrderData using 'new', 'deleted', or 'modified' changeTypes
 */
export const computeNetChangeLedger = (
  currentSowData: ScopeReportLineItem[],
  baselineSowData: ScopeReportLineItem[],
): ScopeReportLineItemWithChangeData[] => {
  const baselineItemsByKey = buildLineItemKeyMap(baselineSowData);
  const currentItemsByKey = buildLineItemKeyMap(currentSowData);
  const resultItems: ScopeReportLineItemWithChangeData[] = [];

  currentSowData.forEach(currentItem => {
    const key = findLineItemKey(currentItem);
    const baselineItem = baselineItemsByKey.get(key);

    if (baselineItem) {
      const blendedEntry = computeBlendedModificationEntry(currentItem, baselineItem);
      if (blendedEntry) {
        resultItems.push(blendedEntry);
      } else {
        resultItems.push(currentItem);
      }
    } else {
      resultItems.push({
        ...currentItem,
        changeOrderData: {
          changeType: 'new',
          changedColumns: { contractor: false, quantity: false, rate: false, amount: true, deferred: false, tenantResponsibility: false },
        },
      });
    }
  });

  baselineSowData.forEach(baselineItem => {
    const key = findLineItemKey(baselineItem);
    if (!currentItemsByKey.has(key)) {
      resultItems.push({
        ...baselineItem,
        productQuantity: -(baselineItem.productQuantity || 0),
        totalLineItemCost: -(baselineItem.totalLineItemCost || 0),
        changeOrderData: {
          changeType: 'deleted',
          changedColumns: { contractor: false, quantity: true, rate: false, amount: true, deferred: false, tenantResponsibility: false },
        },
      });
    }
  });

  return resultItems;
};

const computeBlendedModificationEntry = (
  currentItem: ScopeReportLineItem,
  baselineItem: ScopeReportLineItem,
): ScopeReportLineItemWithChangeData | null => {
  if (!hasLineItemChanged(currentItem, baselineItem)) return null;

  const changedColumns = computeChangedColumns(currentItem, baselineItem);

  const baselineQuantity = baselineItem.productQuantity || 0;
  const currentQuantity = currentItem.productQuantity || 0;
  const baselineTotal = baselineItem.totalLineItemCost || 0;
  const currentTotal = currentItem.totalLineItemCost || 0;

  const baselineHadNoVendorPurchaseOrder = baselineItem.isDeferredLineItem === true && currentItem.isDeferredLineItem === false;
  // The new contractor's PO needs the full current line; processContractorOnlyChanges adds the baseline reversal for the old contractor
  const isContractorReassignment = changedColumns.contractor;

  if (baselineHadNoVendorPurchaseOrder || isContractorReassignment) {
    return {
      ...currentItem,
      changeOrderData: {
        changeType: 'modified',
        baselineQuantity,
        baselineUnitPrice: baselineItem.unitPrice || 0,
        baselineTotalPrice: baselineTotal,
        baselineContractorName: baselineItem.contractorName || '',
        changedColumns,
      },
    };
  }

  const deltaQuantity = currentQuantity - baselineQuantity;
  const deltaAmount = currentTotal - baselineTotal;

  if (changedColumns.rate && !changedColumns.quantity) {
    return {
      ...currentItem,
      productQuantity: 1,
      totalLineItemCost: deltaAmount,
      unitPrice: deltaAmount,
      changeOrderData: {
        changeType: 'modified',
        baselineQuantity,
        baselineUnitPrice: baselineItem.unitPrice || 0,
        baselineTotalPrice: baselineTotal,
        baselineContractorName: baselineItem.contractorName || '',
        changedColumns,
      },
    };
  }

  const blendedUnitPrice = computeBlendedUnitPrice(deltaQuantity, deltaAmount, currentItem.unitPrice || 0);

  return {
    ...currentItem,
    productQuantity: deltaQuantity,
    unitPrice: blendedUnitPrice,
    totalLineItemCost: deltaAmount,
    changeOrderData: {
      changeType: 'modified',
      baselineQuantity,
      baselineUnitPrice: baselineItem.unitPrice || 0,
      baselineTotalPrice: baselineTotal,
      baselineContractorName: baselineItem.contractorName || '',
      changedColumns,
    },
  };
};

const computeBlendedUnitPrice = (
  deltaQuantity: number,
  deltaAmount: number,
  currentUnitPrice: number,
): number => {
  if (Math.abs(deltaQuantity) < 0.01) {
    return currentUnitPrice;
  }
  return deltaAmount / deltaQuantity;
};

const hasLineItemChanged = (
  currentItem: ScopeReportLineItem,
  baselineItem: ScopeReportLineItem,
): boolean => {
  const baselineTotal = baselineItem.totalLineItemCost || 0;
  const currentTotal = currentItem.totalLineItemCost || 0;
  const baselineContractor = baselineItem.contractorName || '';
  const currentContractor = currentItem.contractorName || '';
  const baselineQuantity = baselineItem.productQuantity || 0;
  const currentQuantity = currentItem.productQuantity || 0;
  const baselineRate = baselineItem.unitPrice || 0;
  const currentRate = currentItem.unitPrice || 0;

  const contractorChanged = baselineContractor !== currentContractor;
  const quantityChanged = Math.abs(baselineQuantity - currentQuantity) > 0.01;
  const rateChanged = Math.abs(baselineRate - currentRate) > 0.01;
  const amountChanged = Math.abs(baselineTotal - currentTotal) > 0.01;
  const deferredChanged = (baselineItem.isDeferredLineItem === true) !== (currentItem.isDeferredLineItem === true);
  // KAIDEV-4181: tenantResponsibility is informational and never a change-order trigger.
  const hasProductChanges = baselineItem.productName !== currentItem.productName
    || baselineItem.productSku !== currentItem.productSku
    || baselineItem.productBrand !== currentItem.productBrand;

  return contractorChanged || quantityChanged || rateChanged || amountChanged
    || deferredChanged || hasProductChanges;
};

/** Per-column change flags for ledger/diff rows; property names match change-order column ids. */
const computeChangedColumns = (
  currentItem: ScopeReportLineItem,
  baselineItem: ScopeReportLineItem,
): { contractor: boolean; quantity: boolean; rate: boolean; amount: boolean; deferred: boolean; tenantResponsibility: boolean } => {
  const baselineTotal = baselineItem.totalLineItemCost || 0;
  const currentTotal = currentItem.totalLineItemCost || 0;
  const baselineContractor = baselineItem.contractorName || '';
  const currentContractor = currentItem.contractorName || '';
  const baselineQuantity = baselineItem.productQuantity || 0;
  const currentQuantity = currentItem.productQuantity || 0;
  const baselineRate = baselineItem.unitPrice || 0;
  const currentRate = currentItem.unitPrice || 0;

  return {
    contractor: baselineContractor !== currentContractor,
    quantity: Math.abs(baselineQuantity - currentQuantity) > 0.01,
    rate: Math.abs(baselineRate - currentRate) > 0.01,
    amount: Math.abs(baselineTotal - currentTotal) > 0.01,
    deferred: (baselineItem.isDeferredLineItem === true) !== (currentItem.isDeferredLineItem === true),
    tenantResponsibility: false, // KAIDEV-4181: never highlighted as a change
  };
};

const computeLedgerModificationEntries = (
  currentItem: ScopeReportLineItem,
  baselineItem: ScopeReportLineItem,
): ScopeReportLineItemWithChangeData[] => {
  if (!hasLineItemChanged(currentItem, baselineItem)) return [];

  const changedColumns = computeChangedColumns(currentItem, baselineItem);

  const reversalEntry: ScopeReportLineItemWithChangeData = {
    ...baselineItem,
    productQuantity: -(baselineItem.productQuantity || 0),
    totalLineItemCost: -(baselineItem.totalLineItemCost || 0),
    changeOrderData: {
      changeType: 'reversal',
      baselineQuantity: baselineItem.productQuantity || 0,
      baselineUnitPrice: baselineItem.unitPrice || 0,
      baselineTotalPrice: baselineItem.totalLineItemCost || 0,
      baselineContractorName: baselineItem.contractorName || '',
      changedColumns,
    },
  };

  const restatementEntry: ScopeReportLineItemWithChangeData = {
    ...currentItem,
    changeOrderData: {
      changeType: 'restatement',
      baselineQuantity: baselineItem.productQuantity || 0,
      baselineUnitPrice: baselineItem.unitPrice || 0,
      baselineTotalPrice: baselineItem.totalLineItemCost || 0,
      baselineContractorName: baselineItem.contractorName || '',
      changedColumns,
    },
  };

  return [reversalEntry, restatementEntry];
};

export const buildScopeOverview = ({
  scan,
  propertyProject,
  orgProperty,
  organization,
  optimizedScan,
  contractorsMap,
  latestApprovedScanDate,
  getScanNotesByScanPartId,
}: {
  scan: ApiScan; // Original scan object (contains scanLifecycleStatus, updatedAt, etc.)
  propertyProject: ApiPropertyProject;
  orgProperty: ApiOrgProperty;
  organization: ApiOrg;
  optimizedScan: ScanRootOptimized; // Optimized scan for cost calculations and contractor data
  contractorsMap: ApiOrgContractorsMap;
  latestApprovedScanDate?: string;
  /** The same artifact-eligible notes lookup the line items use; the scope's own notes are filed on the scan root. */
  getScanNotesByScanPartId: ScanPartNotesLookup;
}): ScopeReportOverview => {
  const materialCost = optimizedScan.optimized?.reducedValues?.materialCost ?? 0;
  const laborCost = optimizedScan.optimized?.reducedValues?.laborCost ?? 0;
  const totalProjectCost = materialCost + laborCost;

  const generalContractorId = optimizedScan.defaultContractorsByTrade?.['general']
    || getFirstAssignedGeneralContractor(optimizedScan)
    || 'unassigned';
  const generalContractor = getContractorName(contractorsMap, generalContractorId);

  return {
    // Property Details
    propertyId: orgProperty?.customerMetadata?.propertyId ?? undefined,
    propertyAddress: orgProperty?.address,
    beds: parseMetadataNumber(orgProperty?.customerMetadata?.bedroomCount),
    baths: calculateBathCount(orgProperty?.customerMetadata),
    squareFeet: parseMetadataNumber(orgProperty?.customerMetadata?.squareFeet),
    acres: parseMetadataNumber(orgProperty?.customerMetadata?.acres),
    vintage: parseMetadataNumber(orgProperty?.customerMetadata?.vintage),
    hasGarage: orgProperty?.customerMetadata?.hasGarage ?? undefined,
    // stories: parseMetadataNumber(orgProperty?.customerMetadata?.stories), // TODO: add stories to customer metadata

    // Project Details
    projectName: propertyProject?.name,
    projectStatus: propertyProject?.status,
    projectEstimatedCompletionDate: propertyProject?.customerMetadata?.dueDate ? formatCalendarDate(propertyProject.customerMetadata.dueDate) : undefined,

    // Scan Details
    scanName: scan?.name,
    scanId: scan?.id,
    scanType: propertyProject?.customerMetadata?.projectType?.toUpperCase() || 'Initial Acquisition',
    approvedDate: scan?.scanLifecycleStatus === 'approved'
      ? formatDate(scan?.approvedAt ?? scan?.updatedAt)
      : latestApprovedScanDate
      ? formatDate(latestApprovedScanDate)
      : undefined,
    generalContractor,
    responsibleOrg: organization?.name,
    doorCode: scan?.customerMetadata?.doorCode ?? undefined,
    gateCode: scan?.customerMetadata?.gateCode ?? undefined,

    // Cost Summary
    totalMaterialCost: materialCost,
    totalLaborCost: laborCost,
    totalProjectCost,
    projectBudget: parseMetadataNumber(propertyProject?.customerMetadata?.budget),

    scopeNotes: getPrintedNoteBodies(getScanNotesByScanPartId(optimizedScan.id)),
  };
};

export type OverviewData = ReturnType<typeof buildScopeOverview>;

/**
 * Extract and group all task photos by group name, deduplicating by composite key (taskId + groupId)
 *
 * @param sowData - The grouped SOW data containing task photos
 * @returns Object with group names as keys and arrays of unique task photos as values
 */
export const extractTaskPhotosByGroup = (sowData: GroupedReportLineItems): Record<string, TaskPhotoWithName[]> => {
  const photosByGroup: Record<string, TaskPhotoWithName[]> = {};

  Object.entries(sowData).forEach(([groupName, items]) => {
    const taskPhotoMap = new Map<string, TaskPhotoWithName>();

    items?.forEach(item => {
      if (item && item.taskPhotos?.length && item.taskId) {
        const compositeKey = `${item.groupId}|${item.taskId}`;
        if (!taskPhotoMap.has(compositeKey)) {
          taskPhotoMap.set(compositeKey, {
            taskName: item.taskName,
            taskPhotos: item.taskPhotos,
          });
        }
      }
    });

    const groupPhotos = Array.from(taskPhotoMap.values());
    if (groupPhotos.length > 0) {
      photosByGroup[groupName] = groupPhotos;
    }
  });

  return photosByGroup;
};

/**
 * Extract task photos from a flat array of line items, deduplicating by composite key (groupId|taskId)
 *
 * @param lineItems - Flat array of scope report line items
 * @returns Map with composite key (groupId|taskId) as keys and TaskPhotoWithName as values
 */
export const extractTaskPhotosMap = (lineItems: ScopeReportLineItem[]): Map<string, TaskPhotoWithName> => {
  const taskPhotoMap = new Map<string, TaskPhotoWithName>();

  lineItems.forEach(item => {
    if (item.taskPhotos?.length && item.taskId) {
      const compositeKey = `${item.groupId}|${item.taskId}`;
      if (!taskPhotoMap.has(compositeKey)) {
        taskPhotoMap.set(compositeKey, {
          taskName: item.taskName,
          taskPhotos: item.taskPhotos,
        });
      }
    }
  });

  return taskPhotoMap;
};

export type CostsByProductSupplier = Record<string, { totalMaterialCost: number; totalLaborCost: number }>;
/*
 * Build a map of product supplier IDs to total material and labor costs
 * this runs on Scan lifecycle status change to `approved`
 *
 * @param optimizedScan - The optimized scan data containing groups and tasks
 * @returns Map where keys are product supplier IDs and values are total material and labor costs
 */
export const buildTotalCostsByProductSupplierId = (
  optimizedScan: ScanRootOptimized,
  productSupplierNameMap: Map<string, string> | Record<string, string>,
): CostsByProductSupplier => {
  // Helper to get name from map (supports both Map and Record)
  const getProductSupplierName = (productSupplierId: string): string => {
    if (productSupplierNameMap instanceof Map) {
      return productSupplierNameMap.get(productSupplierId) || '';
    }
    return productSupplierNameMap[productSupplierId] || '';
  };

  const aggregatedCosts: CostsByProductSupplier = {};

  for (const lineItem of getAllScopedLineItems(optimizedScan)) {
    const productSupplierId = lineItem.skuDetails?.productSupplierId;
    if (!productSupplierId || !lineItem.skuDetails?.id) continue;

    const productSupplierName = getProductSupplierName(productSupplierId);
    if (!productSupplierName) continue;

    const totalMaterialCost = lineItem.optimized?.reducedValues?.materialCost ?? 0,
      totalLaborCost = lineItem.optimized?.reducedValues?.laborCost ?? 0;

    if (!aggregatedCosts[productSupplierName]) {
      aggregatedCosts[productSupplierName] = {
        totalMaterialCost: 0,
        totalLaborCost: 0,
      };
    }
    aggregatedCosts[productSupplierName].totalMaterialCost += totalMaterialCost;
    aggregatedCosts[productSupplierName].totalLaborCost += totalLaborCost;
  }

  return aggregatedCosts;
};

export const buildScopeReportLineItemsForBilling = (
  optimizedScan: ScanRootOptimized,
  contractorsMap: ApiOrgContractorsMap,
  productSupplierNameMap: Map<string, string> | Record<string, string>,
): BillingExcelLineItem[] => {
  const shouldExcludeGroup = (group: ScanGroupOptimized): boolean => !isGroupShownOnScopeReport(group);

  const shouldExcludeTask = (task: ScanTaskOptimized): boolean => !isTaskShownOnScopeReport(task);

  const shouldExcludeOption = (option: ScanOptionOptimized): boolean => !isOptionScoped(option);

  const shouldExcludeLineItem = (lineItem: ScanLineItemOptimized): boolean => !isLineItemScoped(lineItem);
  // TODO: we eventually want to make sure deferred items are not included here
  // but our metabase total calculation above was previously excluding them
  // and to make sure numbers match we are leaving them in for now
  // filterDeferredLineItem(lineItem);

  const getProductSupplierName = (productSupplierId: string): string => {
    if (productSupplierNameMap instanceof Map) {
      return productSupplierNameMap.get(productSupplierId) || '';
    }
    return productSupplierNameMap[productSupplierId] || '';
  };

  const getContractorNameForTask = (task: ScanTaskOptimized): string => {
    const contractorId = getContractorIdForTask(task, optimizedScan);
    return getContractorName(contractorsMap, contractorId);
  };

  const allLineItems = (optimizedScan.groups ?? [])
    .filter(group => !shouldExcludeGroup(group))
    .flatMap(group =>
      (group.tasks ?? [])
        .filter(task => !shouldExcludeTask(task))
        .flatMap(task => {
          const contractorName = getContractorNameForTask(task);
          return (task.options ?? [])
            .filter(option => !shouldExcludeOption(option))
            .flatMap(option =>
              (option.lineItems ?? [])
                .filter(lineItem => !shouldExcludeLineItem(lineItem))
                .map(lineItem => {
                  const productSupplierId = lineItem.skuDetails?.productSupplierId;
                  if (!productSupplierId || !lineItem.skuDetails?.id) return null;

                  const productSupplierName = getProductSupplierName(productSupplierId);
                  if (!productSupplierName) return null;

                  return {
                    groupName: getGroupName(group),
                    taskName: getTaskName(task),
                    description: getScopePDFTaskDescName(task, option, lineItem),
                    trade: getTradeForTask(task),
                    glCode: getGlCode(task),
                    lineItemId: lineItem.id,
                    productName: lineItem.skuDetails?.title ?? undefined,
                    productSku: lineItem.skuDetails?.skus?.[0] ?? undefined,
                    productQuantity: getLineItemQuantity(lineItem),
                    unitPrice: getNumericValue(lineItem.values?.price) || 0,
                    totalLineItemCost: lineItem.optimized?.reducedValues?.materialCost,
                    contractorName,
                    productSupplierId,
                    productSupplierName,
                  };
                })
            );
        })
    )
    .filter((row): row is NonNullable<typeof row> => row !== null);

  return allLineItems as unknown as BillingExcelLineItem[];
};
